import api from 'api/instance';
import type { FetchQueryType, FetchResponse } from 'types/common';
import type { SKU, SkuType } from 'types/sku';
import type { Location } from 'types/washhub';

const URL = `${process.env.REACT_APP_API_URL}`;

type SKUResponse = {
  message: string;
  items: SKU[];
};

export const fetchAllSKU = async (
  type: SkuType,
  washHub?: string,
  site?: string,
  activeOnly?: boolean,
  skipWashHub?: boolean,
) => {
  const { data } = await api.get<SKUResponse>(`${URL}/sku/list`, {
    params: {
      type,
      washHub,
      limit: 1000,
      site,
      activeOnly,
      skipWashHub: skipWashHub ?? false,
    },
  });

  return data?.items;
};

export const fetchSkusByManufacturer = async (manufacturerId: string) => {
  const { data } = await api.get<SKUResponse>(`${URL}/sku/list`, {
    params: {
      type: 'item',
      manufacturer: manufacturerId,
    },
  });

  return data?.items ?? [];
};

export const fetchAllPaginatedSKU = async (
  type: SkuType,
  params: FetchQueryType & { activeOnly?: boolean; manufacturer?: string },
) => {
  const { data } = await api.get<FetchResponse<SKU>>(`${URL}/sku/list`, {
    params: {
      type,
      ...params,
    },
  });

  return data;
};

type ExportSkuParams = {
  search?: string;
  activeOnly?: boolean;
};

export const exportItemSkusCsv = async (params: ExportSkuParams) => {
  const { data } = await api.post<string>(`${URL}/sku/export-items`, {
    search: params.search || undefined,
    activeOnly: params.activeOnly,
  });

  return data;
};

export const exportPackageSkusCsv = async (params: ExportSkuParams) => {
  const { data } = await api.post<string>(`${URL}/sku/export-packages`, {
    search: params.search || undefined,
    activeOnly: params.activeOnly,
  });

  return data;
};

type CreateSKUPayload = Partial<{
  name: string;
  code: string;
  size: string;
  type: string;
  color: string;
  designVersion: string;
  material: string;
  poorLines: string;
  weight: string;
}>;

type CreateCaseSKUPayload = Partial<{
  name: string;
  prefix: string;
  description?: string;
  type: string;
}>;

export const createSKU = async (payload: CreateSKUPayload) => {
  const { data } = await api.post(`${URL}/sku`, payload);

  return data;
};

export const createCaseSKU = async (payload: CreateCaseSKUPayload) => {
  const { data } = await api.post(`${URL}/sku/case`, payload);

  return data;
};

export type CreateEquipmentSKUPayload = {
  name: string;
  category: string;
  manufacturer?: string;
};

export const createEquipmentSKU = async (
  payload: CreateEquipmentSKUPayload,
) => {
  const { data } = await api.post(`${URL}/sku/equipment`, payload);

  return data;
};

export const updateEquipmentSKU = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/sku/update-equipment-sku/${_id}`,
    _payload,
  );

  return data;
};

export const deleteSkus = async (ids: string[]) => {
  const { data } = await api.delete(`${URL}/sku/remove-many`, {
    data: { ids },
  });

  return data;
};

export const updateSKU = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/sku/update-item-sku/${_id}`,
    _payload,
  );

  return data;
};

export const updateCaseSKU = async (payload: any) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/sku/update-case-sku/${_id}`,
    _payload,
  );

  return data;
};

type CreatePackageSKUPayload = {
  name: string;
  caseSku: string;
  items?: Array<{ sku: string; quantity: number }>;
};

export const createPackageSKU = async (payload: CreatePackageSKUPayload) => {
  const { data } = await api.post(`${URL}/sku/package`, payload);

  return data;
};

export const updatePackageSKU = async (
  payload: { _id: string } & CreatePackageSKUPayload,
) => {
  const { _id, ..._payload } = payload;

  const { data } = await api.patch(
    `${URL}/sku/update-package-sku/${_id}`,
    _payload,
  );

  return data;
};

export const getItemsForPackages = async (packages: Array<string>) => {
  const { data } = await api.post(`${URL}/sku/get-item-sku-package-sku`, {
    packages,
  });

  return data?.item?.itemSku as Array<SKU>;
};

export const getAssignedWashHubs = async (packageSku: string) => {
  const { data } = await api.post(`${URL}/sku/get-assigned-wash-hub`, {
    packageSku,
  });

  return data?.item as Array<Location>;
};

export const getAllItemsForPackages = async (packages: string[]) => {
  const { data } = await api.post(
    `${URL}/sku/get-all-items-sku-for-package-skus`,
    {
      packages,
    },
  );

  return data?.item?.map((s: any) => s.sku) as Array<SKU>;
};
